import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline'

import logger from './logger'
import { reportErrorDetails } from './errorReporting'

const TopbarMountId = 'loaded-topbar'
const TopbarReactDOMId = 'loaded-topbar-react'

/**
 * Helper component that injects any children into the persistent
 * `#loaded-topbar` mount node defined in the AngularJS host's index.html.
 *
 * The mount node lives outside the AngularJS `ui-view`, so it is far more
 * stable than the React page portal target. We still mirror the resilience
 * approach used in `withAngularCompatibility.tsx`: we wait for the node to
 * exist before rendering and keep a MutationObserver running so that if the
 * node is ever detached/replaced we can reacquire it.
 */
function TopbarInjector({ children }: PropsWithChildren) {
  const [target, setTarget] = useState<Element | null>(null)
  const targetRef = useRef<Element | null>(null)

  useEffect(() => {
    const updateTarget = (newTarget: Element | null) => {
      targetRef.current = newTarget
      setTarget(newTarget)
    }

    const existingTarget = document.getElementById(TopbarMountId)

    if (existingTarget) {
      logger.info('Topbar mount node found, rendering React topbar')
      updateTarget(existingTarget)
    } else {
      logger.info('Watching for the topbar mount node to appear')
    }

    // We always set up a MutationObserver, even when the mount node already
    // exists. This lets us both pick the node up if it appears later and
    // reacquire it if AngularJS ever detaches or replaces it - which would
    // otherwise leave our portal rendering into a detached node.
    const topbarTargetObserver = new MutationObserver(() => {
      const detached = targetRef.current && !targetRef.current.isConnected
      const notYetAcquired = !targetRef.current

      if (detached || notYetAcquired) {
        const newTarget = document.getElementById(TopbarMountId)
        if (newTarget && newTarget !== targetRef.current) {
          if (detached) {
            logger.error(
              'Topbar portal target was detached from the DOM. Reacquiring.'
            )
          }
          updateTarget(newTarget)
        }
      }
    })

    const bodyNode = document.querySelector('body')

    if (bodyNode) {
      topbarTargetObserver.observe(bodyNode, {
        attributes: false,
        childList: true,
        subtree: true,
      })
    } else {
      logger.error('Unable to find body node to inject the React topbar into')
    }

    return () => {
      topbarTargetObserver.disconnect()
    }
  }, [])

  if (target && !target.isConnected) {
    const message =
      'React topbar portal target is not connected to the DOM at render time. Content will be invisible.'
    logger.error(message)
    reportErrorDetails(new Error(message))
  }

  return target
    ? createPortal(
        <ScopedCssBaseline id={TopbarReactDOMId}>{children}</ScopedCssBaseline>,
        target
      )
    : null
}

/**
 * A higher-order-component that ensures the wrapped component is rendered into
 * the persistent `#loaded-topbar` mount node via a React portal.
 * @param Component
 * @returns
 */
export default function withTopbarPortal(Component: FC) {
  return function WithTopbarPortal(props) {
    return (
      <TopbarInjector>
        <Component {...props} />
      </TopbarInjector>
    )
  }
}
